import { useEffect, useRef } from 'react';
import type { Conversation } from '../../shared/types';
import type { CallController, CallTile } from '../hooks/useCall';
import { useChat } from '../state';
import {
  IconClose,
  IconMic,
  IconMicOff,
  IconPhoneOff,
  IconScreen,
  IconSparkles,
  IconSwitchCamera,
  IconVideo,
  IconVideoOff,
} from './icons';
import { Avatar } from './ui';

function VideoTile({ tile }: { tile: CallTile }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const element = videoRef.current;
    const track = tile.video;
    if (!element || !track) return;
    track.attach(element);
    return () => {
      track.detach(element);
    };
  }, [tile.video]);

  return (
    <div className={`tile ${tile.screen ? 'screen' : ''} ${tile.speaking ? 'speaking' : ''}`}>
      {tile.video ? (
        <video ref={videoRef} autoPlay playsInline muted={tile.local} />
      ) : (
        <Avatar name={tile.name} size={64} />
      )}
      <span className="tile-name">
        {tile.name}
        {tile.local ? ' (you)' : ''}
        {tile.screen ? ' · screen' : ''}
      </span>
    </div>
  );
}

interface CallPanelProps {
  call: CallController;
  conversation: Conversation;
  onClose: () => void;
}

export function CallPanel({ call, conversation, onClose }: CallPanelProps) {
  const capabilities = useChat((state) => state.capabilities);
  const activeCamera = call.cameras.find((camera) => camera.deviceId === call.activeCameraId);
  const switchTitle = !call.camera
    ? 'Turn on the camera to switch cameras'
    : !call.canSwitchCamera
      ? 'Only one camera is available'
      : activeCamera?.facing === 'user'
        ? 'Switch to back camera'
        : 'Switch to front camera';

  return (
    <div className="call-panel">
      <div className="call-head">
        <span className="live-dot" />
        <strong>{call.connected ? 'In call' : call.status === 'connecting' ? 'Connecting…' : 'Call'}</strong>
        <span className="call-room">{conversation.title}</span>
        <button className="icon-btn" onClick={onClose} aria-label="Close call">
          <IconClose size={18} />
        </button>
      </div>

      {call.error ? <div className="call-error">{call.error}</div> : null}

      <div className={`tiles ${call.tiles.length <= 1 ? 'single' : ''}`}>
        {call.tiles.map((tile) => (
          <VideoTile key={tile.id} tile={tile} />
        ))}
        {!call.tiles.length ? <div className="call-hint">Waiting for participants…</div> : null}
      </div>

      <div className="call-controls">
        <button className={`ctrl ${call.mic ? '' : 'off'}`} onClick={() => void call.toggleMic()} title="Microphone">
          {call.mic ? <IconMic /> : <IconMicOff />}
        </button>
        <button className={`ctrl ${call.camera ? '' : 'off'}`} onClick={() => void call.toggleCamera()} title="Camera">
          {call.camera ? <IconVideo /> : <IconVideoOff />}
        </button>
        <button
          className="ctrl"
          onClick={() => void call.switchCamera()}
          disabled={!call.camera || !call.canSwitchCamera || call.switchingCamera}
          title={switchTitle}
          aria-label={switchTitle}
        >
          <IconSwitchCamera />
        </button>
        <button className={`ctrl ${call.screen ? 'on' : ''}`} onClick={() => void call.toggleScreen()} title="Share screen">
          <IconScreen />
        </button>
        <button
          className={`ctrl ${call.agent ? 'on' : ''}`}
          onClick={() => void call.toggleAgent()}
          disabled={!capabilities?.ai.enabled || call.agentBusy}
          title="AI voice agent"
        >
          <IconSparkles />
          <span className="ctrl-label">{call.agent ? 'AI on' : 'AI'}</span>
        </button>
        <button className="ctrl hangup" onClick={onClose} title="Leave call">
          <IconPhoneOff />
        </button>
      </div>
    </div>
  );
}
